/* corex-munequera-io.js · Lectura de los archivos del cliente en el personalizador de muñequeras.
   Devuelve la imagen recortada a su dibujo (sin márgenes transparentes) Y lo que hace falta para colocarla bien
   (CorexMunequeraModel.placeImage): la caja del recorte dentro de la página, los píxeles de la página y su tamaño
   físico en mm (página del PDF, o ppp guardados en el PNG/JPG). Así un diseño entra con sus medidas reales y, si viene
   sobre la plantilla, en su sitio. corex-textil-editor-io.js (camisetas) recorta pero no conserva la posición.
   PDF: solo la página 1, a 300 ppp, con pdf.js bajo demanda (CorexTextilIO.importModule: el minificador de Shopify
   rompe el import() dinámico de los assets). */
(function (global) {
  'use strict';

  var M = global.CorexMunequeraModel;
  var IO = global.CorexTextilIO;
  var MAX_IMG_BYTES = 25 * 1024 * 1024;
  var MAX_PDF_BYTES = 60 * 1024 * 1024;
  var ACCEPTED = ['image/png', 'image/jpeg', 'image/webp'];
  /* Build «legacy» de pdf.js (transpilada para navegadores no tan recientes: Safari/iPhone de hace unos años); cdnjs
     no la publica, jsDelivr sí. Versión fijada. */
  var PDFJS_BASE = 'https://cdn.jsdelivr.net/npm/pdfjs-dist@6.3.289/legacy/build/';
  var PDF_DPI = 300;
  var PT_TO_MM = 25.4 / 72;
  var MAX_PIXELS = 40 * 1000 * 1000;
  var HEADER_BYTES = 256 * 1024;
  var ALPHA_MIN = 8;

  function userError(msg) { return Object.assign(new Error(msg), { userFacing: true }); }
  function isPdf(file) { return file.type === 'application/pdf' || /\.pdf$/i.test(file.name || ''); }
  function imageFromSrc(src) {
    return new Promise(function (resolve, reject) {
      var img = new Image();
      img.onload = function () { resolve(img); };
      img.onerror = function () { reject(userError('La imagen parece dañada y no se puede abrir.')); };
      img.src = src;
    });
  }

  /* Recorta al dibujo (alfa > ALPHA_MIN) un canvas de página. → { src, img, box, page } */
  function trimToDrawing(canvas, alpha) {
    var w = canvas.width, h = canvas.height;
    var box = { x: 0, y: 0, w: w, h: h };
    if (alpha) {
      var found = IO.alphaBox(canvas.getContext('2d', { willReadFrequently: true }).getImageData(0, 0, w, h).data, w, h, ALPHA_MIN);
      if (!found) return Promise.reject(userError('El archivo está vacío o es totalmente transparente: no hay nada que imprimir.'));
      box = found;
    }
    var out = canvas;
    if (box.w !== w || box.h !== h) {
      out = document.createElement('canvas');
      out.width = box.w;
      out.height = box.h;
      out.getContext('2d').drawImage(canvas, box.x, box.y, box.w, box.h, 0, 0, box.w, box.h);
    }
    var src = out.toDataURL('image/png');
    return imageFromSrc(src).then(function (img) { return { src: src, img: img, box: box, page: { w: w, h: h } }; });
  }

  function readImage(file) {
    if (ACCEPTED.indexOf(file.type) === -1) return Promise.reject(userError('Formato no admitido. Sube tu diseño en PNG, JPG, WebP o PDF.'));
    if (file.size > MAX_IMG_BYTES) return Promise.reject(userError('La imagen supera 25 MB. Prueba con una versión más ligera.'));
    var dpi = file.slice(0, HEADER_BYTES).arrayBuffer().then(function (b) { return M.headerDpi(new Uint8Array(b)); }, function () { return null; });
    var data = new Promise(function (resolve, reject) {
      var reader = new FileReader();
      reader.onerror = function () { reject(userError('No se ha podido leer la imagen. Inténtalo de nuevo.')); };
      reader.onload = function () { resolve(reader.result); };
      reader.readAsDataURL(file);
    });
    return Promise.all([data.then(imageFromSrc), dpi, data]).then(function (r) {
      var img = r[0], w = img.naturalWidth, h = img.naturalHeight;
      var pageMm = r[1] ? { w: w / r[1] * 25.4, h: h / r[1] * 25.4 } : null;
      /* JPG: sin transparencia, la imagen entera es el dibujo (y se conserva el archivo original). */
      if (file.type === 'image/jpeg') return { src: r[2], img: img, box: { x: 0, y: 0, w: w, h: h }, page: { w: w, h: h }, pageMm: pageMm };
      var c = document.createElement('canvas');
      c.width = w;
      c.height = h;
      c.getContext('2d').drawImage(img, 0, 0);
      return trimToDrawing(c, true).then(function (t) { return Object.assign(t, { pageMm: pageMm }); });
    });
  }

  var pdfjsPromise = null;
  function loadPdfJs() {
    if (!pdfjsPromise) {
      pdfjsPromise = IO.importModule(PDFJS_BASE + 'pdf.min.mjs').then(function (lib) {
        lib.GlobalWorkerOptions.workerSrc = PDFJS_BASE + 'pdf.worker.min.mjs';
        return lib;
      }).catch(function () {
        pdfjsPromise = null;
        throw userError('No se ha podido abrir el lector de PDF. Revisa la conexión e inténtalo de nuevo.');
      });
    }
    return pdfjsPromise;
  }
  function pdfError(err) {
    var name = err && err.name;
    if (name === 'PasswordException') return 'El PDF está protegido con contraseña. Guárdalo sin protección y vuelve a subirlo.';
    if (name === 'InvalidPDFException') return 'El PDF parece dañado y no se puede abrir.';
    return 'No se ha podido leer el PDF. Comprueba que no esté protegido o dañado, o súbelo en PNG o JPG.';
  }
  function readPdf(file) {
    if (file.size > MAX_PDF_BYTES) return Promise.reject(userError('El PDF supera 60 MB. Prueba con una versión más ligera.'));
    return Promise.all([loadPdfJs(), file.arrayBuffer()])
      .then(function (r) { return r[0].getDocument({ data: new Uint8Array(r[1]) }).promise; })
      .then(function (pdf) {
        return pdf.getPage(1).then(function (page) {
          var base = page.getViewport({ scale: 1 });
          var scale = PDF_DPI / 72;
          if (base.width * base.height * scale * scale > MAX_PIXELS) scale = Math.sqrt(MAX_PIXELS / (base.width * base.height));
          var vp = page.getViewport({ scale: scale });
          var c = document.createElement('canvas');
          c.width = Math.floor(vp.width);
          c.height = Math.floor(vp.height);
          /* Fondo transparente (pdf.js pinta blanco por defecto y, con el canvas en vez del contexto, sin alfa). */
          return page.render({ canvasContext: c.getContext('2d'), viewport: vp, intent: 'print', background: 'rgba(0,0,0,0)' }).promise
            .then(function () { return trimToDrawing(c, true); })
            .then(function (t) {
              var pages = pdf.numPages;
              pdf.loadingTask.destroy();
              return Object.assign(t, { pageMm: { w: base.width * PT_TO_MM, h: base.height * PT_TO_MM }, pages: pages });
            });
        }).catch(function (err) { pdf.loadingTask.destroy(); throw err; });
      })
      .catch(function (err) { throw err && err.userFacing ? err : userError(pdfError(err)); });
  }

  /* → { name, src, img, naturalWidth, naturalHeight, box, page, pageMm, pages } */
  function readDesignFile(file) {
    return (isPdf(file) ? readPdf(file) : readImage(file)).then(function (r) {
      return Object.assign(r, { name: file.name, naturalWidth: r.img.naturalWidth, naturalHeight: r.img.naturalHeight, pages: r.pages || 1 });
    });
  }

  global.CorexMunequeraIO = { readDesignFile: readDesignFile, isPdf: isPdf, loadPdfJs: loadPdfJs };
})(this);
